// ABOUTME: Smart keyword matching system for detecting trade types from user input
// ABOUTME: Uses existing trade categories and provides confidence scoring for matches

import { detectTradeCategory, TRADE_CATEGORIES, TradeCategory } from '@/lib/tradeCategories';
import { debug } from '@/lib/utils';

export interface TradeMatch {
  category: TradeCategory;
  confidence: number;
  matchedKeywords: string[];
  reason: string;
}

export interface AnalysisResult {
  matches: TradeMatch[];
  topMatch: TradeMatch | null;
  originalText: string;
  keywords: string[];
}

/**
 * Analyze user input text and find matching trade categories
 */
export function analyzeUserInput(text: string): AnalysisResult {
  if (!text || text.trim().length === 0) {
    return {
      matches: [],
      topMatch: null,
      originalText: text,
      keywords: []
    };
  }

  const normalizedText = text.toLowerCase();
  const words = normalizedText.split(/\s+/);

  debug.log('Analyzing user input:', { text, words });

  // Find all potential matches with scoring
  const matches: TradeMatch[] = [];

  for (const category of TRADE_CATEGORIES) {
    if (category.isLegacy) continue;

    const matchedKeywords: string[] = [];
    let score = 0;

    // Check for keyword matches
    for (const keyword of category.keywords) {
      const keywordLower = keyword.toLowerCase();

      // Exact word match (highest score)
      if (words.includes(keywordLower)) {
        matchedKeywords.push(keyword);
        score += 10;
      }
      // Partial word match
      else if (normalizedText.includes(keywordLower)) {
        matchedKeywords.push(keyword);
        score += 5;
      }
      // Word starts with keyword
      else if (words.some(word => word.startsWith(keywordLower))) {
        matchedKeywords.push(keyword);
        score += 3;
      }
    }

    // Check for display name match
    if (normalizedText.includes(category.displayName.toLowerCase())) {
      matchedKeywords.push(category.displayName);
      score += 15; // High score for display name match
    }

    // Only include categories with matches
    if (matchedKeywords.length > 0) {
      // Calculate confidence based on score and number of matches
      const confidence = Math.min(score / 10, 1); // Cap at 1.0

      let reason = `Found ${matchedKeywords.length} keyword${matchedKeywords.length > 1 ? 's' : ''}`;
      if (matchedKeywords.includes(category.displayName)) {
        reason = `Direct match for "${category.displayName}"`;
      }

      matches.push({
        category,
        confidence,
        matchedKeywords: [...new Set(matchedKeywords)], // Remove duplicates
        reason
      });
    }
  }

  // Sort by confidence (highest first)
  matches.sort((a, b) => b.confidence - a.confidence);

  // Extract all matched keywords for display
  const allKeywords = [...new Set(matches.flatMap(m => m.matchedKeywords))];

  const result: AnalysisResult = {
    matches,
    topMatch: matches.length > 0 ? matches[0] : null,
    originalText: text,
    keywords: allKeywords
  };

  debug.log('Analysis result:', {
    matchCount: matches.length,
    topMatch: result.topMatch?.category.displayName,
    confidence: result.topMatch?.confidence,
    keywords: allKeywords
  });

  return result;
}

/**
 * Get multiple trade suggestions from user input (for showing options)
 */
export function getTopMatches(text: string, maxMatches: number = 3): TradeMatch[] {
  const analysis = analyzeUserInput(text);
  return analysis.matches.slice(0, maxMatches);
}

/**
 * Simple function that mimics the existing detectTradeCategory but with confidence
 */
export function detectTradeCategoryWithConfidence(text: string): { category: TradeCategory | undefined; confidence: number } {
  const analysis = analyzeUserInput(text);

  if (analysis.topMatch) {
    return {
      category: analysis.topMatch.category,
      confidence: analysis.topMatch.confidence
    };
  }

  return {
    category: undefined,
    confidence: 0
  };
}

/**
 * Extract problem-specific keywords that aren't trade types
 */
export function extractProblemKeywords(text: string): string[] {
  const words = text.toLowerCase().split(/\s+/);
  const problemKeywords: string[] = [];

  // Common problem indicators
  const problemWords = [
    'broken', 'leaking', 'not working', 'stuck', 'blocked', 'cracked',
    'noisy', 'dripping', 'loose', 'damaged', 'install', 'replace',
    'repair', 'fix', 'urgent', 'emergency', 'quickly', 'asap'
  ];

  for (const word of words) {
    if (problemWords.some(p => word.includes(p))) {
      problemKeywords.push(word);
    }
  }

  return problemKeywords;
}
